import React, { useEffect, useState } from "react";
import { Modal3 } from "../../../../../components/DIsplay/Modal/Modal3";
import Button1 from "../../../../../components/form/Button1";
import CommonFreeTextInput from "../../../../../components/common/inputs/CommonFreeTextInput";
import { enqueueSnackbar } from "notistack";
import { formatMoney } from "../../../../../features/utils/formatMoney";

function toNumber(value) {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) ? num : 0;
}

function sanitizeAmount(val) {
  return String(val || "")
    .replace(/[^0-9.]/g, "")
    .replace(/(\..*)\./g, "$1");
}

function formatAmountInput(value) {
  if (!value && value !== 0) return "";
  const n = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(n)) return "";
  return n.toLocaleString();
}

function AmountInput({ label, placeholder, value, onChange }) {
  const [focused, setFocused] = useState(false);

  return (
    <CommonFreeTextInput
      label={label}
      placeholder={placeholder}
      value={focused ? value || "" : formatAmountInput(value)}
      onChange={(val) => onChange(sanitizeAmount(val))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

function EditFlightBookingPriceModal({ open, setOpen, booking, onSave }) {
  const [payableInput, setPayableInput] = useState("");
  const [customerPayableInput, setCustomerPayableInput] = useState("");
  const [markup, setMarkup] = useState(0);
  const [forceEditCustomerPayable, setForceEditCustomerPayable] =
    useState(false);

  const payable = toNumber(payableInput);
  const computedCustomerPayable = payable + markup;
  const customerPayable = forceEditCustomerPayable
    ? toNumber(customerPayableInput)
    : computedCustomerPayable;

  useEffect(() => {
    if (!open) return;
    const originalPayable = toNumber(booking?.payable);
    const originalCustomerPayable = toNumber(booking?.customerPayable);
    setPayableInput(
      booking?.payable != null && booking?.payable !== ""
        ? String(booking.payable)
        : "",
    );
    setMarkup(originalCustomerPayable - originalPayable);
    setCustomerPayableInput(String(originalCustomerPayable));
    setForceEditCustomerPayable(false);
  }, [open, booking?.payable, booking?.customerPayable]);

  const toggleForceEdit = () => {
    setForceEditCustomerPayable((prev) => {
      const next = !prev;
      if (next) {
        setCustomerPayableInput(String(computedCustomerPayable));
      }
      return next;
    });
  };

  const handleSave = () => {
    if (payableInput === "" || Number.isNaN(payable) || !Number.isFinite(payable)) {
      enqueueSnackbar("Payable amount is required", { variant: "warning" });
      return;
    }
    if (payable < 0) {
      enqueueSnackbar("Payable cannot be negative", { variant: "warning" });
      return;
    }

    if (forceEditCustomerPayable) {
      if (
        customerPayableInput === "" ||
        Number.isNaN(customerPayable) ||
        !Number.isFinite(customerPayable)
      ) {
        enqueueSnackbar("Customer payable is required", { variant: "warning" });
        return;
      }
      if (customerPayable < 0) {
        enqueueSnackbar("Customer payable cannot be negative", {
          variant: "warning",
        });
        return;
      }
    }

    onSave?.({
      payable,
      paymentAmount: payable,
      pricedAmountNgn: payable,
      customerPayable,
    });
    setOpen(false);
  };

  return (
    <Modal3 open={open} setOpen={setOpen} title="Edit price">
      <div className="p-4 sm:p-6 flex flex-col gap-4 min-w-[280px] sm:min-w-[420px]">
        {!forceEditCustomerPayable ? (
          <div className="rounded-lg border border-[#E8EEF5] bg-[#F8FAFC] p-3 text-sm text-[#6D7C94]">
            Markup kept from original price:{" "}
            <span className="font-semibold text-[#1A1A1A]">
              {formatMoney(markup)}
            </span>
          </div>
        ) : null}

        <AmountInput
          label="Payable"
          placeholder="Enter payable amount"
          value={payableInput}
          onChange={setPayableInput}
        />

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600">Customer payable</span>
            <button
              type="button"
              onClick={toggleForceEdit}
              className={`text-xs font-semibold px-2.5 py-1 rounded-md border transition ${
                forceEditCustomerPayable
                  ? "bg-primary1 text-white border-primary1"
                  : "bg-white text-primary1 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {forceEditCustomerPayable ? "Using force edit" : "Force edit"}
            </button>
          </div>

          {forceEditCustomerPayable ? (
            <AmountInput
              label=""
              placeholder="Enter customer payable"
              value={customerPayableInput}
              onChange={setCustomerPayableInput}
            />
          ) : (
            <div className="h-12 px-4 rounded-lg border border-[#DBDBDB] bg-[#EEF2F7] text-sm text-[#1A1A1A] flex items-center justify-between">
              <span>{formatMoney(customerPayable)}</span>
              <span className="text-xs text-[#6D7C94]">payable + markup</span>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <Button1
            variant="outlined"
            className="!border-primary !text-primary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button1>
          <Button1 onClick={handleSave}>Save</Button1>
        </div>
      </div>
    </Modal3>
  );
}

export default EditFlightBookingPriceModal;
